import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import styles from './form.module.css';

const DEFAULT_HOTSPOT_COLOR = '#0ea5e9';
const hexToRgba = (hex, alpha) => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return `rgba(14,165,233,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const HotspotForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const imageUrl = content?.settings?.imageUrl || '';
    const hotspots = content?.settings?.hotspots || [];
    const imageNaturalWidth = content?.settings?.imageNaturalWidth;
    const imageNaturalHeight = content?.settings?.imageNaturalHeight;
    const hasAspectRatio = imageNaturalWidth && imageNaturalHeight;
    const markerColor = content?.settings?.hotspotMarkerColor || DEFAULT_HOTSPOT_COLOR;

    const [selected, setSelected] = useState(() => userAnswer?.hotspot || null);

    useEffect(() => {
        setSelected(userAnswer?.hotspot || null);
    }, [userAnswer]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && selected === userAnswer.hotspot;
    const submitDisabled = isPreview || isLocked || !selected || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleHotspotClick = (id) => {
        if (isDisabled) return;
        setSelected(id);
    };

    const handleSubmit = () => {
        if (submitDisabled) return;
        onSubmit({ hotspot: selected });
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Click on the correct area'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {!isDisabled && <div className={styles.description}>Click on a hotspot to select your answer.</div>}
            <div
                className={styles.imageTargetWrap}
                style={hasAspectRatio ? { aspectRatio: `${imageNaturalWidth} / ${imageNaturalHeight}` } : undefined}
            >
                <img
                    src={imageUrl}
                    alt="Hotspot target"
                    className={styles.imageTargetImg}
                    style={hasAspectRatio ? { height: '100%', maxHeight: 'none' } : undefined}
                    draggable={false}
                />
                {hotspots.map((hs) => (
                    <button
                        key={hs.id}
                        type="button"
                        onClick={() => handleHotspotClick(hs.id)}
                        disabled={isDisabled}
                        title={hs.label || hs.id}
                        className={`${styles.hotspotMarker} ${selected === hs.id ? styles.selected : ''}`}
                        style={{
                            left: `${hs.x}%`,
                            top: `${hs.y}%`,
                            width: `${(hs.radius || 3) * 2}%`,
                            aspectRatio: '1',
                            borderColor: selected === hs.id ? markerColor : undefined,
                            background: selected === hs.id ? hexToRgba(markerColor, 0.6) : undefined,
                            cursor: isDisabled ? 'default' : 'pointer',
                        }}
                        aria-label={`Hotspot: ${hs.label || hs.id}`}
                        aria-pressed={selected === hs.id}
                    />
                ))}
            </div>
            {selected && (
                <div style={{ textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                    Selected: <strong>{hotspots.find(h => h.id === selected)?.label || selected}</strong>
                </div>
            )}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default HotspotForm;
